import { Link, useLocation } from "wouter";
import { LogOut, Settings, LayoutDashboard, Menu, X } from "lucide-react";
import { CowboyHatIcon } from "@/components/cowboy-hat-icon";
import { Button } from "@/components/ui/button";
import { useState, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import freshOrderLogo from "@assets/Logo_-new_clear_1764787161621.png";
import { OrganizationContext } from "@/contexts/OrganizationContext";
import { useAdminAuth } from "@/contexts/AdminAuthContext";

export function Navigation() {
  const [location, setLocation] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Try to get organization from context (will be null if not within provider)
  const orgContext = useContext(OrganizationContext);
  
  // Extract org slug from URL if present (handles /org/:orgSlug/admin/... pattern)
  const orgMatch = location.match(/^\/org\/([^/]+)\/admin/);
  const orgSlug = orgMatch ? orgMatch[1] : null;
  const isOrgScopedAdmin = !!orgSlug;
  
  // Only show navigation on admin pages - hide completely on phone entry and customer pages
  const isLegacyAdminPage = location.startsWith("/admin");
  const isLegacyStaffPage = location === "/dashboard" || location === "/accounts";
  const showNavigation = isLegacyAdminPage || isLegacyStaffPage || isOrgScopedAdmin;
  
  // Reactively track admin auth state (set by AdminRoute after server-side verify-session)
  const { isAdminAuthenticated } = useAdminAuth();

  // Gate the "Clyde" tab on the org actually running Clyde. Only fetched on
  // authed, org-scoped admin pages; on error/loading the tab stays hidden (fail-safe),
  // so dormant tenants never see a half-built section. (Hook must run before the early
  // return below — Rules of Hooks.)
  const { data: agentSettings } = useQuery<{ agentEnabled?: boolean }>({
    queryKey: ["/api/agent/settings"],
    enabled: isOrgScopedAdmin && isAdminAuthenticated,
    staleTime: 60_000,
    retry: false,
  });
  const showAgentTab = !!agentSettings?.agentEnabled;

  // Only show navigation header on admin pages when authenticated
  if (!showNavigation || !isAdminAuthenticated) {
    return null;
  }
  
  // Build base path for navigation links
  const basePath = isOrgScopedAdmin ? `/org/${orgSlug}/admin` : "/admin";
  
  // Get org branding from OrganizationContext (preferred) or fall back to session storage
  const contextLogoUrl = orgContext?.organization?.logoUrl;
  const orgBranding = sessionStorage.getItem("orgBranding");
  const sessionBranding = orgBranding ? JSON.parse(orgBranding) : null;
  const logoSrc = contextLogoUrl || sessionBranding?.logoUrl || freshOrderLogo;

  const handleLogout = async () => {
    try {
      if (orgSlug) {
        await fetch(`/org/${orgSlug}/api/admin/logout`, {
          method: "POST",
          credentials: "include",
        });
      }
    } catch {
      // Ignore network errors — still clear local state
    }
    sessionStorage.removeItem("adminUser");
    sessionStorage.removeItem("orgBranding");
    sessionStorage.removeItem("currentOrgSlug");
    sessionStorage.removeItem("currentUser");
    // B12 (M-A5): full-page nav on logout, not SPA setLocation, so the TanStack
    // Query cache is wiped. An SPA logout left another tenant's cached PII in
    // memory for whoever logged in next in the same tab.
    window.location.href = "/";
  };

  // Helper to check if current location matches a nav item
  const isActive = (path: string) => {
    const fullPath = `${basePath}${path}`;
    if (path === "") {
      // For "Dashboard" link which goes to base admin path
      return location === basePath || location === "/dashboard";
    }
    return location === fullPath || location.startsWith(fullPath);
  };

  return (
    // viewport-fit=cover (client/index.html) extends the page under the iOS status bar /
    // Dynamic Island, so this sticky header needs the safe-area inset or its controls
    // render under the camera cutout and can't be tapped (admin PWA on iPhone).
    <header
      className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src={logoSrc} 
              alt="Dashboard" 
              className="h-12 w-auto max-w-[120px] object-contain"
              onError={(e) => {
                // Fall back to default logo if custom logo fails to load
                e.currentTarget.src = freshOrderLogo;
              }}
            />
          </div>
          
          {/* Desktop Navigation - Hidden on mobile */}
          <nav className="hidden md:flex items-center justify-center flex-1 px-2 sm:px-8">
            <div className="flex items-center space-x-1 sm:space-x-1">
              <Link href={basePath} className={`font-medium px-2 sm:px-3 py-2 text-sm sm:text-base transition-colors border-b-2 border-transparent ${
                isActive("") ? "text-cn-brand-green border-b-cn-brand-green" : "text-gray-600 hover:text-cn-brand-green hover:border-b-cn-brand-green"
              }`}>
                Dashboard
              </Link>
              
              <Link href={`${basePath}/orders`} className={`text-gray-600 hover:text-orange-600 font-medium px-2 sm:px-3 py-2 text-sm sm:text-base transition-colors border-b-2 border-transparent ${
                isActive("/orders") ? "text-orange-600 border-b-orange-600" : "hover:border-b-orange-600"
              }`}>
                Orders
              </Link>
              
              <Link href={`${basePath}/products`} className={`text-gray-600 hover:text-sky-400 font-medium px-2 sm:px-3 py-2 text-sm sm:text-base transition-colors border-b-2 border-transparent ${
                isActive("/products") ? "text-sky-400 border-b-sky-400" : "hover:border-b-sky-400"
              }`}>
                Procurement
              </Link>
              
              <Link href={`${basePath}/accounts`} className={`font-medium px-2 sm:px-3 py-2 text-sm sm:text-base transition-colors border-b-2 border-transparent ${
                isActive("/accounts") ? "text-[#FBBF24] border-b-[#FBBF24]" : "text-gray-600 hover:text-[#FBBF24] hover:border-b-[#FBBF24]"
              }`}>
                Accounts
              </Link>

              {showAgentTab && (
                <Link href={`${basePath}/agent`} className={`font-medium px-2 sm:px-3 py-2 text-sm sm:text-base transition-colors border-b-2 border-transparent flex items-center gap-1 ${
                  isActive("/agent") ? "text-violet-600 border-b-violet-600" : "text-gray-600 hover:text-violet-600 hover:border-b-violet-600"
                }`}>
                  <CowboyHatIcon className="h-4 w-4" />
                  Clyde
                </Link>
              )}
            </div>
          </nav>

          {/* Mobile Hamburger Button - Only visible on mobile */}
          <div className="flex md:hidden flex-1 justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-gray-600 hover:text-gray-900"
              data-testid="button-mobile-menu"
            >
              {isMobileMenuOpen ? <X className="h-8 w-8" /> : <Menu className="h-8 w-8" />}
            </Button>
          </div>
          
          {/* Actions */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            <Link href={`${basePath}/settings`} className={`p-2 text-gray-500 hover:text-cn-brand-blue hover:bg-blue-200 rounded-md transition-colors ${
              isActive("/settings") ? "bg-blue-200 text-cn-brand-blue" : ""
            }`}>
              <Settings className="h-5 w-5" />
            </Link>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-gray-500 hover:text-red-600 hover:bg-red-50 p-2"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay - Only visible when hamburger is clicked */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-gray-200 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            <Link 
              href={basePath}
              className={`block px-3 py-3 rounded-md text-base font-medium transition-colors ${
                isActive("") ? "text-cn-brand-green bg-green-50" : "text-gray-600 hover:text-cn-brand-green hover:bg-green-50"
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="mobile-link-dashboard"
            >
              Dashboard
            </Link>
            
            <Link 
              href={`${basePath}/orders`}
              className={`block px-3 py-3 rounded-md text-base font-medium transition-colors ${
                isActive("/orders") ? "text-orange-600 bg-orange-50" : "text-gray-600 hover:text-orange-600 hover:bg-orange-50"
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="mobile-link-orders"
            >
              Orders
            </Link>
            
            <Link 
              href={`${basePath}/products`}
              className={`block px-3 py-3 rounded-md text-base font-medium transition-colors ${
                isActive("/products") ? "text-sky-400 bg-sky-50" : "text-gray-600 hover:text-sky-400 hover:bg-sky-50"
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="mobile-link-items"
            >
              Items
            </Link>
            
            <Link 
              href={`${basePath}/accounts`}
              className={`block px-3 py-3 rounded-md text-base font-medium transition-colors ${
                isActive("/accounts") ? "text-[#FBBF24] bg-yellow-50" : "text-gray-600 hover:text-[#FBBF24] hover:bg-yellow-50"
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="mobile-link-accounts"
            >
              Accounts
            </Link>

            {showAgentTab && (
              <Link
                href={`${basePath}/agent`}
                className={`block px-3 py-3 rounded-md text-base font-medium transition-colors ${
                  isActive("/agent") ? "text-violet-600 bg-violet-50" : "text-gray-600 hover:text-violet-600 hover:bg-violet-50"
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-link-agent"
              >
                Clyde
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
